import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        setProfile({
          id: userDoc.id,
          ...userDoc.data()
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    fetchProfile();
  }, [user, fetchProfile]);

  const updateDietaryPreferences = async (dietStyle, allergies, restrictions) => {
    if (!user) throw new Error('No user authenticated');

    try {
      const userDocRef = doc(db, 'users', user.uid);
      
      await updateDoc(userDocRef, {
        'dietaryProfile.dietStyle': dietStyle || null,
        'dietaryProfile.allergies': allergies || [],
        'dietaryProfile.avoidIngredients': restrictions || [],
        updatedAt: new Date()
      });

      await fetchProfile();
      return { success: true };
    } catch (err) {
      console.error('Error updating dietary preferences:', err);
      throw err;
    }
  };

  const updateProfileBasics = async (updates) => {
    if (!user) throw new Error('No user authenticated');

    try {
      const userDocRef = doc(db, 'users', user.uid);
      
      await updateDoc(userDocRef, {
        ...updates,
        updatedAt: new Date()
      });

      await fetchProfile();
      return { success: true };
    } catch (err) {
      console.error('Error updating profile:', err);
      throw err;
    }
  };

  const updateNotificationSettings = async (settings) => {
    if (!user) throw new Error('No user authenticated');

    try {
      const userDocRef = doc(db, 'users', user.uid);
      
      await updateDoc(userDocRef, {
        notificationSettings: settings,
        updatedAt: new Date()
      });

      await fetchProfile();
      return { success: true };
    } catch (err) {
      console.error('Error updating notification settings:', err);
      throw err;
    }
  };

  const updatePrivacySettings = async (settings) => {
    if (!user) throw new Error('No user authenticated');

    try {
      const userDocRef = doc(db, 'users', user.uid);
      
      await updateDoc(userDocRef, {
        privacySettings: settings,
        updatedAt: new Date()
      });

      await fetchProfile();
      return { success: true };
    } catch (err) {
      console.error('Error updating privacy settings:', err);
      throw err;
    }
  };

  return {
    profile,
    loading,
    error,
    fetchProfile,
    updateDietaryPreferences,
    updateProfileBasics,
    updateNotificationSettings,
    updatePrivacySettings
  };
};
